import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import Modal from "../../components/ui-components/Modal";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import { getCategories, getSourceTypes, createCustomSourceType, updateCustomSourceType, deleteCustomSourceType } from "../allocation/taxonomyApi";

const DRIVER_KEYS = ["cpu_seconds", "storage_gb_days", "request_count", "inference_count", "tokens", "bytes_billed", "slot_ms"];

// Sentinel for the "All" category filter (shows every source type, regardless of category).
const ALL_CATEGORY = "__all__";

const CostSourceTaxonomyPage = () => {
  const queryClient = useQueryClient();
  const catsQ = useQuery(["taxonomy-categories"], getCategories);
  const typesQ = useQuery(["taxonomy-source-types"], getSourceTypes);

  const categories = useMemo(() => catsQ.data?.categories || [], [catsQ.data]);
  const types = useMemo(() => typesQ.data?.source_types || [], [typesQ.data]);
  const [selected, setSelected] = useState(ALL_CATEGORY); // "All" is the default
  const [searchQuery, setSearchQuery] = useState("");
  const activeCat = selected;
  const rows = useMemo(
    () => (activeCat === ALL_CATEGORY ? types : types.filter((t) => t.category_id === activeCat)),
    [types, activeCat]
  );

  // Client-side search across the category-filtered rows (matches the /budgets table).
  const searchLower = searchQuery.trim().toLowerCase();
  const filteredRows = useMemo(
    () =>
      searchLower
        ? rows.filter((t) =>
            [t.display_name, t.provider, t.default_driver_key, t.default_confidence]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(searchLower)
          )
        : rows,
    [rows, searchLower]
  );

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode, else row being edited
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [driverKey, setDriverKey] = useState("");
  const [shared, setShared] = useState(false);
  const [confidence, setConfidence] = useState("medium");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // source_type_id pending confirm
  const [deleteError, setDeleteError] = useState(null);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setCategory(activeCat && activeCat !== ALL_CATEGORY ? activeCat : (categories[0]?.category_id || ""));
    setDriverKey("");
    setShared(false);
    setConfidence("medium");
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row.display_name);
    setCategory(row.category_id);
    setDriverKey(row.default_driver_key || "");
    setShared(row.default_shared_flag || false);
    setConfidence(row.default_confidence || "medium");
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setName("");
    setError(null);
  };

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true); setError(null);
    const payload = {
      category_id: category,
      display_name: name.trim(),
      default_driver_key: driverKey || null,
      default_shared_flag: shared,
      default_confidence: confidence,
    };
    try {
      if (editing) {
        await updateCustomSourceType(editing.source_type_id, payload);
      } else {
        await createCustomSourceType(payload);
      }
      closeModal();
      queryClient.invalidateQueries(["taxonomy-source-types"]);
    } catch (e) {
      setError(e?.status === 409 ? "A source type with that name already exists." : editing ? "Failed to update type" : "Failed to create type");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteError(null);
    setConfirmDeleteId(row.source_type_id);
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await deleteCustomSourceType(id);
      queryClient.invalidateQueries(["taxonomy-source-types"]);
    } catch (e) {
      setDeleteError(
        e?.status === 409
          ? (e?.serverBody?.error?.message || "This type is still in use — reassign first.")
          : "Failed to delete type"
      );
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteId(null);
  };

  // Categories render as the table's segmented filter switch (matching the /budgets table).
  const filterOptions = useMemo(
    () => [
      { value: ALL_CATEGORY, label: "All" },
      ...categories.map((c) => ({ value: c.category_id, label: c.display_name })),
    ],
    [categories]
  );

  const columnDefs = useMemo(
    () => [
      {
        accessor: "display_name",
        Header: () => <span>Type</span>,
        Cell: ({ row }) => {
          const t = row.original;
          return (
            <span className="font-medium text-text">
              {t.display_name}
              {!t.system_managed && <span className="ml-2 text-xs font-normal text-success">Custom</span>}
            </span>
          );
        },
        flex: 1,
        minWidth: 220,
      },
      {
        accessor: "provider",
        Header: () => <span>Provider</span>,
        Cell: ({ value }) => <span className="text-text-2">{value || "—"}</span>,
        width: 130,
        minWidth: 100,
      },
      {
        accessor: "default_driver_key",
        Header: () => <span>Driver</span>,
        Cell: ({ value }) => <span className="text-text-2">{value || "—"}</span>,
        width: 150,
        minWidth: 120,
      },
      {
        accessor: "default_shared_flag",
        Header: () => <span>Shared</span>,
        Cell: ({ value }) => <span className="text-text-2">{value ? "yes" : "no"}</span>,
        width: 90,
        minWidth: 80,
      },
      {
        accessor: "default_confidence",
        Header: () => <span>Confidence</span>,
        Cell: ({ value }) => <span className="text-text-2">{value}</span>,
        width: 120,
        minWidth: 100,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        Cell: ({ row }) => {
          const t = row.original;
          if (t.system_managed) return null;
          if (confirmDeleteId === t.source_type_id) {
            return (
              <div className="flex justify-end gap-1.5">
                <button type="button" onClick={handleDeleteConfirm}
                  className="px-2 py-0.5 text-xs rounded-ctrl bg-danger text-white hover:bg-danger">
                  Confirm
                </button>
                <button type="button" onClick={handleDeleteCancel}
                  className="px-2 py-0.5 text-xs rounded-ctrl border border-border text-text hover:bg-surface-2">
                  Cancel
                </button>
              </div>
            );
          }
          return (
            <div className="flex justify-end gap-1.5">
              <button type="button" onClick={() => openEdit(t)}
                className="px-2 py-0.5 text-xs rounded-ctrl border border-border text-text hover:bg-surface-2">
                Edit
              </button>
              <button type="button" onClick={() => handleDeleteClick(t)}
                className="px-2 py-0.5 text-xs rounded-ctrl border border-danger/40 text-danger hover:bg-danger-soft">
                Delete
              </button>
            </div>
          );
        },
        width: 150,
        minWidth: 140,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmDeleteId]
  );

  const isLoading = catsQ.isLoading || typesQ.isLoading;
  const loadError = catsQ.error || typesQ.error;
  const noun = filteredRows.length === 1 ? "type" : "types";

  return (
    <>
      <AdministrationHeader title="Cost Source Taxonomy" showPeriodSelector={false} />
      <div className="w-full p-8">
        {deleteError && (
          <div className="mb-3 px-4 py-2 text-sm text-danger bg-danger-soft border border-danger/30 rounded-ctrl">
            {deleteError}
          </div>
        )}
        <TableCard
          title="Source types"
          subtitle={
            isLoading
              ? "Loading…"
              : searchQuery
                ? `Showing ${filteredRows.length} ${noun} matching "${searchQuery}"`
                : `Showing ${filteredRows.length} ${noun}`
          }
          primaryAction={{ label: "Add custom type", icon: icon({ name: "plus" }), onClick: openCreate }}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search source types…"
          filterValue={activeCat}
          onFilterChange={setSelected}
          filterOptions={filterOptions}
          filterAriaLabel="Filter by category"
          isLoading={isLoading}
          error={loadError}
          errorMessage="Error loading source types"
          onRetry={() => queryClient.invalidateQueries(["taxonomy-source-types"])}
        >
          {rows.length === 0 ? (
            <EmptyState
              icon={icon({ name: "tags", style: "light" })}
              title="No types in this category"
              message="Add a custom type to extend this category."
              action={{ label: "Add custom type", onClick: openCreate, icon: icon({ name: "plus", style: "regular" }) }}
            />
          ) : filteredRows.length === 0 ? (
            <EmptyState
              icon={icon({ name: "magnifying-glass" })}
              title="No matching types"
              message={`No source types match "${searchQuery}".`}
              action={{
                label: "Clear search",
                onClick: () => setSearchQuery(""),
                icon: icon({ name: "xmark", style: "regular" }),
                variant: "secondary",
              }}
            />
          ) : (
            <FlexTable
              columns={columnDefs}
              data={filteredRows}
              getRowId={(t) => t.source_type_id}
            />
          )}
        </TableCard>
      </div>

      <Modal
        show={modalOpen}
        close={closeModal}
        title={editing ? "Edit custom source type" : "Add custom source type"}
        description={
          editing
            ? <span>Edit the details for this custom source type.</span>
            : <span>Adds a customer-defined type under the selected category. It inherits the category&rsquo;s semantics.</span>
        }
        confirmButton={saving ? (editing ? "Saving…" : "Creating…") : (editing ? "Save" : "Create")}
        confirmAction={submit}
        cancelButton="Cancel"
      >
        <div className="mt-2 space-y-3">
          <div>
            <label htmlFor="ct-name" className="block text-xs font-medium text-text-2 mb-1">Type name</label>
            <input id="ct-name" aria-label="type name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm" />
          </div>

          <div>
            <label htmlFor="ct-category" className="block text-xs font-medium text-text-2 mb-1">Category</label>
            <select id="ct-category" aria-label="category" value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm bg-card">
              {categories.map((c) => (
                <option key={c.category_id} value={c.category_id}>{c.display_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="ct-driver" className="block text-xs font-medium text-text-2 mb-1">Default driver</label>
            <select id="ct-driver" aria-label="default driver" value={driverKey} onChange={(e) => setDriverKey(e.target.value)}
              className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm bg-card">
              <option value="">— none —</option>
              {DRIVER_KEYS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input id="ct-shared" type="checkbox" aria-label="shared" checked={shared} onChange={(e) => setShared(e.target.checked)}
              className="rounded border-border" />
            <label htmlFor="ct-shared" className="text-xs font-medium text-text-2">Shared cost</label>
          </div>

          <div>
            <label htmlFor="ct-confidence" className="block text-xs font-medium text-text-2 mb-1">Default confidence</label>
            <select id="ct-confidence" aria-label="confidence" value={confidence} onChange={(e) => setConfidence(e.target.value)}
              className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm bg-card">
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </div>

          {error && <p className="text-xs text-danger mt-2">{error}</p>}
        </div>
      </Modal>
    </>
  );
};

export default CostSourceTaxonomyPage;
